import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { VerificationDialog } from '../verification-dialog/verification-dialog'
import { SharedService } from '../../../services/shared-service/shared.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ApiUrlHelper } from '../../../common/api-url-helper';
import { CommonService } from '../../../services/common/common.service';
import { CookieService } from 'ngx-cookie-service';
import { EncryptionService } from '../../../services/EncryptionService/encryption.service';
import { RecaptchaWidgetComponent } from '../../../component/recaptcha-widget/recaptcha-widget';
import { CaptchaService } from '../../../services/captcha-service/captcha.service';

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.html',
  styleUrl: './login.scss'
})

export class Login implements OnInit {

  loginForm: FormGroup | any;
  submitted: boolean = false;
  labelConstants: any;
  errorConstants: any;
  isShowPassword: boolean = false;
  isCaptchaVerified: boolean = false;
  year: any = new Date().getFullYear();
  @ViewChild('recaptchaWidget') recaptchaWidget?: RecaptchaWidgetComponent;

  constructor(
    private readonly dialog: MatDialog,
    private readonly sharedService: SharedService,
    private readonly fb: FormBuilder,
    private readonly spinner: NgxSpinnerService,
    private readonly toastr: ToastrService,
    private readonly commonService: CommonService,
    private readonly router: Router,
    private readonly api: ApiUrlHelper,
    private readonly cookieService: CookieService,
    private readonly encryptionService: EncryptionService,
    public readonly captchaService: CaptchaService
  ) { }

  ngOnInit(): void {
    const userId = this.sharedService.getValue('userId');
    const fromResetFlow = this.sharedService.getValue('fromResetFlow');
    if (fromResetFlow === 'true') {
      this.sharedService.removeValue('fromResetFlow');
    } else if (userId && userId.trim() !== '') {
      this.router.navigate(['/dashboard']);
      return;
    }
    this.cookieService.delete('password', '/');
    this.loginForm = this.fb.group({
      UserEmail: ['', [Validators.required, Validators.email, Validators.pattern(/^(?=.{1,30}@)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
      UserPassword: ['', [Validators.required]],
      keepMeLoggedIn: [false]
    });

    this.sharedService.setData('Login');
    this.isCaptchaVerified = !this.captchaService.isEnabled();
    this.fetchData();
  }

  get loginFormControls() {
    return this.loginForm?.controls;
  }

  onCaptchaResolved(): void {
    this.isCaptchaVerified = true;
  }

  onCaptchaExpired(): void {
    this.isCaptchaVerified = false;
  }

  submitLoginForm() {
    this.submitted = true;
    if (this.loginForm.invalid || !this.loginForm.value.UserEmail || !this.loginForm.value.UserPassword) {
      return;
    }

    if (this.captchaService.isEnabled() && !this.recaptchaWidget?.hasValidToken()) {
      this.toastr.error('Please complete the security verification.');
      return;
    }

    this.submitted = false;
    this.spinner.show();
  
    const objData = {
      UserEmail: this.loginForm.value.UserEmail,
      Password: this.loginForm.value.UserPassword,
      CaptchaToken: this.recaptchaWidget?.getToken() ?? ''
    };
  
    const apiUrl = this.api.apiUrl.Account.UserLogin;
    this.sharedService.clearStorage();
  
    this.commonService.doPost(apiUrl, objData).pipe().subscribe({
      next: (response) => {
        if (response.success) {
          this.spinner.hide();
  
          const userId = response.data?.UserId;
          const uniqueId = response.data?.uniqueId;
          const emailId = this.loginForm.value.UserEmail;
          const path = '/';
          if (this.loginForm.value.keepMeLoggedIn) {
            this.cookieService.set('keepMeLoggedIn', 'yes', { expires: 30, path });

            this.cookieService.set('emailId', emailId, { expires: 30, path });
          } else {
            this.cookieService.delete('keepMeLoggedIn', path);
            this.cookieService.delete('emailId', path);
            this.cookieService.delete('password', path);
            this.loginForm.patchValue({ UserPassword: '' });
          }
          this.openDialog(uniqueId, emailId, userId);
        } else {
          this.spinner.hide();
          this.recaptchaWidget?.reset();
          this.toastr.error(response.message);
        }
      },
      error: (er) => {
        this.spinner.hide();
        this.recaptchaWidget?.reset();
        console.error(er);
        this.sharedService.clearStorage();
      },
    });
  }

  onForgotPassword() {
    this.router.navigate(['forgot-password'])
  }

  togglePasswordVisibility() {
    this.isShowPassword = !this.isShowPassword;
  }

  openDialog(uniqueId: string, emailId: string, userId: number) {
    const dialogRef = this.dialog.open(VerificationDialog, {
      maxWidth: '560px',
      disableClose: true,
      width: '70%',
      data: { uniqueId, emailId, userId }
    });

    dialogRef.afterClosed().subscribe();
  }

  fetchData() {
    const keepMeLoggedIn = this.cookieService.get('keepMeLoggedIn');
    const savedEmailRaw = this.cookieService.get('emailId');
    let savedEmail = savedEmailRaw;
    if (savedEmailRaw && savedEmailRaw.includes('=')) {
      try {
        savedEmail = this.encryptionService.Decrypt(savedEmailRaw);
      } catch {
        savedEmail = savedEmailRaw;
      }
    }

    if (keepMeLoggedIn === 'yes' && savedEmail) {
      this.loginFormControls['keepMeLoggedIn'].setValue(true);
      this.loginFormControls['UserEmail'].setValue(savedEmail);
      this.loginFormControls['UserPassword'].setValue('');
    } else {
      this.loginFormControls['UserEmail'].setValue('');
      this.loginFormControls['UserPassword'].setValue('');
      this.loginFormControls['keepMeLoggedIn'].setValue(false);
    }
  }

  trimInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const trimmedValue = input.value.trim();

    const controlName = input.getAttribute('formControlName');
    if (controlName && this.loginFormControls[controlName]) {
      this.loginFormControls[controlName].setValue(trimmedValue, { emitEvent: false });
    }
  }
}